import { useState } from "react";
import { Filter, Search, X } from "lucide-react";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import { Button } from "../button";
import { Input, Select } from "../input";
import { Tooltip } from "../tooltip";
import { ActiveFilterChips } from "./ActiveFilterChips";
import { MultiSelectFilter } from "./MultiSelectFilter";
import type { FilterDef, FilterDefs, FilterSet } from "@/hooks/useFilterSet";

/**
 * The list toolbar, ported from bd-admin's arrangement so every list in every agency
 * app reads the same way: search on the left, a filter button carrying an active count
 * beside it, primary actions right-aligned, and the filters themselves in a collapsible
 * panel underneath rather than permanently occupying the row.
 *
 * The panel matters more than it looks: a list with six filters shown at all times
 * pushes the data below the fold, and the count badge is what stops a collapsed panel
 * from hiding the fact that a view *is* filtered.
 */

/** Search box with a clear affordance — bd-admin's `TableSearch`. */
export function TableSearch({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
}) {
  const t = useT();

  // Narrower than the full-width box it started as, then widened again once the pipeline's
  // controls joined its row (2026-08-25): wide enough that the placeholder is not clipped,
  // narrow enough to leave the row to the controls beside it.
  return (
    <div className="relative w-full max-w-[22rem]">
      <Search
        className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
        aria-hidden
      />
      <Input
        type="text"
        aria-label={label ?? t("table.search")}
        value={value}
        placeholder={placeholder ?? t("table.searchPlaceholder")}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 pr-8 pl-9"
      />
      {value && (
        <button
          type="button"
          aria-label={t("table.clearSearch")}
          onClick={() => onChange("")}
          className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/** Filter toggle with an active-count badge, so a collapsed panel still tells the truth. */
export function FilterButton({
  activeCount,
  open,
  onToggle,
  activeFilters,
}: {
  activeCount: number;
  open: boolean;
  onToggle: () => void;
  activeFilters: string[];
}) {
  const t = useT();

  return (
    // The shared tooltip, which lists the active filters one per line — the native `title`
    // this used to carry was unreachable by keyboard, which is exactly the reader who cannot
    // see the count badge either.
    <Tooltip
      content={
        activeCount > 0 ? (
          <span className="block">
            {activeFilters.map((filter) => (
              <span key={filter} className="block">
                {filter}
              </span>
            ))}
          </span>
        ) : (
          t("table.filter")
        )
      }
    >
      <Button
        variant="outline"
        size="icon"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={activeCount > 0 ? t("table.filterCount", activeCount) : t("table.filter")}
        className={cn(
          "relative h-9 w-9 shrink-0",
          activeCount > 0 && "border-primary text-primary",
          open && "bg-accent",
        )}
      >
        <Filter className="h-4 w-4" aria-hidden />
        {activeCount > 0 && (
          <span className="bg-primary text-primary-foreground absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-medium">
            {activeCount}
          </span>
        )}
      </Button>
    </Tooltip>
  );
}

export function FilterField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-muted-foreground text-xs font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * The complete toolbar for a filtered screen. Renders the filter controls from the same
 * declarative definitions the filter seam uses, so the controls, the URL and the API
 * query cannot describe different filters.
 *
 * Typed on `FilterSet`, not `ServerTable`: the board filters without paginating or
 * sorting, and asking it for a page it does not have is what kept the two pipeline views
 * on two different toolbars. A paginated list's `table` satisfies this interface as it is.
 */
export function ListToolbar({
  table,
  defs,
  searchPlaceholder,
  actions,
  chips = true,
}: {
  table: FilterSet;
  defs: FilterDefs;
  searchPlaceholder?: string;
  /** Primary actions — "New client" and the like. Right-aligned, as in bd-admin. */
  actions?: React.ReactNode;
  /**
   * Whether the toolbar draws the active-filter chips itself.
   *
   * The pipeline says no (2026-08-26): its chips sit immediately above the board or the list,
   * below the strip, where the `Filtered` pill used to be — so what is filtering is read next
   * to the thing it filters rather than four blocks above it. Every other screen has nothing
   * between its toolbar and its table, so for them "under the toolbar" *is* "above the
   * content" and they keep the default.
   */
  chips?: boolean;
}) {
  const t = useT();
  const entries = Object.entries(defs);
  const searchEntry = entries.find(([, def]) => def.type === "search");
  // A type predicate rather than a plain filter, so the panel branches below know the
  // search variant (whose `label` is optional) has already been excluded.
  //
  // `hidden` definitions are excluded too: they are reachable only by URL — an analytics
  // tile's click-through — and they keep their chip and their place in the active count, so
  // nothing about the view is concealed by the absence of a control. See `FilterDef`.
  const panelEntries = entries.filter(
    (entry): entry is [string, Exclude<FilterDef, { type: "search" }>] =>
      entry[1].type !== "search" && !entry[1].hidden,
  );

  // Open by default when something is already filtering, so a shared URL does not
  // arrive looking unfiltered.
  const [open, setOpen] = useState(table.activeFilterCount > 0 && panelEntries.length > 0);

  return (
    <div className="min-w-0">
      {/* Breaks at `lg`, not at the browser's mercy (2026-08-25): below that the actions take
          their own line instead of squeezing the year selector down to "26" and the search box
          to an icon. `basis-full lg:basis-auto` is what forces the break — flex-wrap alone let
          the children shrink first and only wrap once they had nothing left to give. */}
      <div className="mb-3 flex flex-wrap items-center gap-2 lg:gap-4">
        <div className="flex min-w-0 flex-1 basis-full items-center gap-2 lg:basis-auto">
          {searchEntry && (
            <TableSearch
              value={table.filters[searchEntry[0]] ?? ""}
              onChange={(value) => table.setFilter(searchEntry[0], value)}
              placeholder={searchPlaceholder}
            />
          )}
          {panelEntries.length > 0 && (
            <FilterButton
              activeCount={table.activeFilterCount}
              open={open}
              onToggle={() => setOpen(!open)}
              activeFilters={table.activeFilterLabels}
            />
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 flex-wrap items-center gap-2 lg:ml-auto">{actions}</div>
        )}
      </div>

      {/* Beneath the row, above the panel: the chips say which filters are on even when
          the panel is shut, which is the state a shared URL most often arrives in. */}
      {chips && <ActiveFilterChips filters={table} />}

      {open && panelEntries.length > 0 && (
        <div className="bg-card mb-4 rounded-md border p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {panelEntries.map(([key, def]) => {
              const id = `filter-${key}`;

              if (def.type === "multi") {
                return (
                  <FilterField key={key} label={def.label} htmlFor={id}>
                    <MultiSelectFilter
                      id={id}
                      label={def.label}
                      options={def.options}
                      selected={table.values[key] ?? []}
                      onChange={(values) => table.setValues(key, values)}
                    />
                  </FilterField>
                );
              }

              if (def.type === "select") {
                return (
                  <FilterField key={key} label={def.label} htmlFor={id}>
                    <Select
                      id={id}
                      value={table.filters[key] ?? ""}
                      onChange={(event) => table.setFilter(key, event.target.value)}
                      className="h-9"
                    >
                      <option value="">{t("common.all")}</option>
                      {def.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FilterField>
                );
              }

              if (def.type === "bool") {
                /*
                 * Two values in a select, not a checkbox (2026-08-26).
                 *
                 * Its neighbours are all popover triggers of the same height, and a bare
                 * checkbox in that row read as a different kind of control — a setting rather
                 * than a filter. A select also gives the *off* state a name: "All" says what a
                 * cleared checkbox only implies, which matters most for the stalled filter,
                 * whose off state is "stalled and not stalled alike".
                 */
                return (
                  <FilterField key={key} label={def.label} htmlFor={id}>
                    <Select
                      id={id}
                      value={table.filters[key] === "true" ? "true" : ""}
                      onChange={(event) => table.setFilter(key, event.target.value === "true" ? "true" : "")}
                      className="h-9"
                    >
                      <option value="">{t("common.all")}</option>
                      <option value="true">{def.label}</option>
                    </Select>
                  </FilterField>
                );
              }

              return (
                <FilterField key={key} label={def.label} htmlFor={id}>
                  <Input
                    id={id}
                    value={table.filters[key] ?? ""}
                    onChange={(event) => table.setFilter(key, event.target.value)}
                    className="h-9"
                  />
                </FilterField>
              );
            })}

            {/* Clear-filters is the grid's last item, not a row of its own beneath it: a
                floating row spent a full row of vertical height on one link, above data that
                the panel is already pushing down. Bottom-aligned in its cell, so it sits on
                the same line as the controls rather than beside their labels. */}
            {table.activeFilterCount > 0 && (
              <div className="flex items-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={table.clearFilters}
                  className="text-muted-foreground h-9 px-3 text-xs"
                >
                  {t("state.clearFilters")}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
